<template>
  <div class="main recommendations">
    <div class="side-header">{{ $t('recommendations-header') }}</div>

    <div class="search-and-sort-input-wrapper">
      <div class="search-input">
        <input v-model="searchValue" v-on:input="search" type="text" :placeholder="$t('search')">
        <i class="fal fa-search fa-lg"></i>
        <div v-if="searchValue && searchValue.length > 0 && filterEnabled" class="search-warning">
          <i class="fal fa-exclamation-triangle"/>
          {{ $t('filter-active') }}
        </div>
      </div>

      <custom-select :prefix="$t('sort-after')"
                     :options="sortValues"
                     :default="sortValue"
                     :translate-values="true"
                     v-on:input="sortValue=$event;sort()">
      </custom-select>
    </div>
    <div class="wrapper">
      <div class="filter-container">
        <custom-button class="showFilterButton" v-if="mq.current === 'mobile'" :onClicked="toggleFilter"
                       :icon="filterVisible ? 'fal fa-chevron-up fa-2x' :  'fal fa-chevron-down fa-2x'">Filter
        </custom-button>
        <themes-filter v-show="filterVisible" ref="themeFilter" v-bind:type="'recommendations'"
                       v-bind:reset-filter="resetFilter"
                       v-bind:networker-count="totalEntries"
                       v-bind:apply-filter="applyFilter" v-bind:is-loading="isLoading"></themes-filter>
      </div>

      <div class="recommendationslist vld-parent">

        <loading v-model:active="isLoading"
                 :is-full-page="false"
                 color="var(--primary-color)">
        </loading>


        <div class="empty-recommendations-header"
             v-if="limitedRecommendations.length === 0 && limitedNetworkers.length === 0 && !isLoading">
          <h3>{{
              filterEnabled ? $t('empty-filter-results') : searchValue ? $t("empty-search-results") : $t("no-recommendations-available")
            }}</h3>
        </div>


        <div v-if="limitedRecommendations.length > 0" class="recommendations-header">
          <h3>{{ recommendationsTitle }} </h3>
        </div>


        <transition-group name="animatedList" tag="div"
                          class="transition-list"
                          v-bind:css="false"
                          v-on:before-enter="animationBeforeEnter"
                          v-on:enter="animationEnter"
                          v-on:leave="animationLeave">
          <networkers-card v-for="(recommendation, index) in limitedRecommendations" v-bind:networker="recommendation"
                           v-bind:data-index="index"
                           v-bind:go-to-profile="openProfileDialog" v-bind:is-recommendation="true"
                           :key="recommendation.id"
                           v-bind:hide-recommendation="hideRecommendation"></networkers-card>
        </transition-group>


        <div v-if="limitedNetworkers.length > 0" class="networker-header">
          <h3 v-if="limitedRecommendations.length > 0"> {{ networkersTitle }}
          </h3>
        </div>


        <transition-group name="animatedList" tag="div"
                          class="transition-list"
                          v-bind:css="false"
                          v-on:before-enter="animationBeforeEnter"
                          v-on:enter="animationEnter"
                          v-on:leave="animationLeave">
          <networkers-card v-for="(networker, index) in limitedNetworkers" v-bind:networker="networker"
                           v-bind:data-index="index" :key="networker.id"
                           v-bind:go-to-profile="openProfileDialog"></networkers-card>
        </transition-group>

        <div class="paginator-wrapper">
          <div class="paginator"
               v-if="!isLoading && (this.limitedNetworkers.length !== 0 || this.limitedRecommendations.length !== 0) ">

            <custom-select
                :suffix="$t('entries-per-page')"
                :options="entriesPerPageOptions"
                :default="entriesPerPage"
                v-on:input="entriesPerPage=$event; get()">
            </custom-select>
            <div class="page-count">
              <span>{{ $t('page') }} {{ currentPage }} {{ $t('of') }}  {{ maxPages }}</span>
              <div class="arrows">
                <i v-on:click="showPreviousPage" class="fal fa-chevron-left fa-2x left-arrow "></i>
                <i v-on:click="showNextPage" class="fal fa-chevron-right fa-2x right-arrow"></i>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>


    <custom-dialog :id="'confirm-exclude-dialog'" ref="confirmExcludeDialog" :on-submit="excludeRecommendation"
                   :submit-button-text="$t('dialog-ok')">
      <span class="header">  {{ $t('hide-recommendation') }} </span>
      <div> {{ $t('hide-recommendation-text') }}</div>
    </custom-dialog>
  </div>
</template>

<script>
import backend from "../../mm-backend";
import MatchScale from "../../components/MatchScale";
import matomo from "../../matomo/tracker";
import fallbackProfileImage from '../../assets/img/person.svg';
import {networkersMixIn} from "../../mixins/networkers-mixin";
import ThemesFilter from "../../themes/filter/themes-filter";
import NetworkersCard from "../../components/cards/NetworkersCard";
import {eventBus} from "../../main";
import {isDefined} from "@/utils";

export default {
  name: "Recommendation",
  components: {
    NetworkersCard,
    ThemesFilter,
    MatchScale
  },
  mixins: [networkersMixIn],
  data() {
    return {};
  },
  computed: {
    limitedRecommendations() {
      return this.recommendations.slice((this.currentPage - 1) * this.entriesPerPage, this.currentPage * this.entriesPerPage);
    },
    limitedNetworkers() {
      let startIndex = (this.currentPage - 1) * this.entriesPerPage;
      // We only show recommendations on the first page
      if (this.currentPage > 1) {
        startIndex = startIndex - this.recommendations.length;
      }
      let maxIndex = (this.currentPage * this.entriesPerPage) - this.recommendations.length;
      if (!isDefined(this.networkers)) {
        return [];
      }
      return this.networkers.slice(startIndex, maxIndex);
    },
  },
  mounted: function () {
    eventBus.on('hide_user', this.reloadFilter);
    eventBus.on('unhide_user', this.reloadFilter);
    eventBus.on('recommendations_updated', this.reloadFilter);
    eventBus.on('reload-networkers', this.reloadFilter);
    this.get();
  },
  beforeUnmount() {
    eventBus.off('unhide_user', this.reloadFilter);
    eventBus.off('hide_user', this.reloadFilter);
    eventBus.off('recommendations_updated', this.reloadFilter);
    eventBus.off('reload-networkers', this.reloadFilter);
    backend.cancelRequests();
  },
  methods: {
    setFallBackImageForUser(userId) {
      this.$refs['img' + userId].src = fallbackProfileImage;
      this.$forceUpdate();
    },
    beforeGet() {
      this.getObservable = backend.matches.bind(backend);
      this.params = {
        confirmedOnly: false,
        pageSize: this.maxPagesToDisplay * this.entriesPerPage,
        page: 0,
        sort: this.sortValue
      };
    },
    afterGet() {
      matomo.trackRecommendationList();
    }
  }
}

</script>

<style scoped lang="less">


.transition-list {
  width: 100%;
}

.recommendations {

  .wrapper {
    display: flex;
    flex-flow: row;
    justify-content: flex-start;
    flex-direction: row;
    width: 100%;

    .recommendationslist {
      display: flex;
      min-width: 500px;
      min-height: 100px;
      flex-wrap: wrap;
      height: fit-content;
      margin-left: 29px;
      width: 100%;

      .empty-recommendations-header {
        > h3 {
          margin-top: 48px;
          color: var(--primary-color);
          padding-left: 0px;
          font-weight: 400;
          font-size: 20px;
        }
      }

      .recommendations-header, .networker-header {
        flex-basis: 100%;
        font-weight: 700;
        display: flex;
        align-content: flex-start;
        padding-left: 0;
        min-height: 48.8px;

      }

      .recommendations-header h3, .networker-header h3 {
        margin: 0 0 20px 0;
      }
    }
  }
}
</style>
